/**
 * `session-lost.ts` — staff-session-slice-1 (2026-09-27). The ONE place the
 * SPA decides that its Supabase session is gone and leaves cleanly, and the
 * one place that names the sign-out SCOPE.
 *
 * # Why this exists
 *
 * On 2026-09-27 a Sign-out tap on a phone deleted every session of that
 * auth user (supabase-js's `signOut()` defaults to `scope: 'global'` —
 * GoTrue `DELETE FROM auth.sessions WHERE user_id`), and a desktop tab on
 * the other host kept its dead token in localStorage. The layout handled
 * only the stale-STUDIO codes; a 401 `whoami_unauthorized` set an error
 * string and still rendered every page, whose cache hooks re-fetched
 * `/api/catalog` once per round-trip — ~20 requests a second, 1,457 log
 * lines that morning — instead of going back to `/login`.
 *
 * # Contract
 *
 *   - `classifySessionLoss(status, code)` — pure. A 401 of any kind, or a
 *     403 carrying one of the stale-studio codes, is a lost session.
 *     Everything else (a page refusal, a 500, a 404) is NOT and must keep
 *     its page-level error rendering.
 *   - `handleSessionLost(reason)` — clears the shared client cache (so no
 *     studio's data can render under the next session), signs out THIS
 *     DEVICE ONLY, and routes to `/login` with the stale-session banner.
 *     Latched: a burst of concurrent 401s (every mounted hook fails at
 *     once) produces one sign-out and one navigation. Re-arms afterwards.
 *   - `signOutThisDevice()` / `signOutEverywhere()` — the two scopes,
 *     named. The layout's Logout button is the FIRST; the second is an
 *     explicit, separately-labelled action. Never call
 *     `supabase.auth.signOut()` bare anywhere in the SPA.
 *   - `registerSessionLostNavigator(fn)` — the router's `navigate`, wired
 *     by the layout on mount so the bounce stays an in-app navigation.
 *     With nothing registered the fallback is a full `location.assign`,
 *     which is also correct (it drops every in-memory state).
 *
 * DOM-free apart from the two seams (`./supabase`, the navigator), so the
 * decision logic is unit-tested under the root `*.test.ts` runner like its
 * siblings in this directory.
 */

import { supabase } from "./supabase";
import { clearCache } from "./resource-cache";

/**
 * The server's "this session cannot act in any studio" codes (HTTP 403 from
 * `/api/whoami`): a db:reset between sign-in and now, an admin-side studio
 * replacement, or a person no `staff_allowlist` row names at all
 * (`studio_unresolved`).
 *
 * `studio_unselected` is deliberately NOT here (staff-person-login-studio-
 * switcher, 2026-09-27). It is the 403 every OTHER route answers when the
 * person belongs to several studios and this request selected none — no
 * `/s/<slug>/` prefix, hence no `X-Layer-Studio`, or a prefix naming a studio
 * they are not in. That is a LIVE session that needs a choice, and the
 * layout renders the chooser for it off `/api/whoami` (which answers 200 with
 * `studio: null` rather than this code). Adding it here would sign the
 * person out for opening the wrong URL. Pinned by `session-lost.test.ts`.
 */
export const STALE_SESSION_CODES: ReadonlySet<string> = new Set([
  "studio_not_found",
  "studio_stale_session",
  "studio_unresolved",
]);

/** The code the bounce carries when the server gave none. */
export const SESSION_LOST_DEFAULT_REASON = "whoami_unauthorized";

export interface SessionLoss {
  /** The `code` the `/login` banner explains (see `STALE_MESSAGES` there). */
  readonly reason: string;
}

/**
 * Whether an API failure means the session is gone. 401 = the server
 * rejected the bearer (`whoami_unauthorized`: deleted session, expired or
 * malformed token, or no token at all); 403 + a stale-studio code = the
 * token is valid but its studio binding is not.
 */
export function classifySessionLoss(
  status: number,
  code: string | null,
): SessionLoss | null {
  if (status === 401) return { reason: code ?? SESSION_LOST_DEFAULT_REASON };
  if (code !== null && STALE_SESSION_CODES.has(code)) return { reason: code };
  return null;
}

/** Where a lost session lands: the login page with its explanatory banner. */
export function loginPathFor(reason: string): string {
  return `/login?reason=stale_session&code=${encodeURIComponent(reason)}`;
}

/**
 * Sign out on THIS device/browser only. The server deletes just the session
 * this tab's token names; sessions on other devices, and on the other
 * dashboard host in the same browser, stay alive. supabase-js removes the
 * local copy even when the server answers 401/403/404 (already dead).
 */
export async function signOutThisDevice(): Promise<void> {
  await supabase.auth.signOut({ scope: "local" });
}

/**
 * Sign out EVERYWHERE — every session of this auth user on every device
 * and host. The explicit action behind its own button; never the default.
 */
export async function signOutEverywhere(): Promise<void> {
  await supabase.auth.signOut({ scope: "global" });
}

type Navigator = (path: string) => void;

let navigator: Navigator | null = null;
let leaving = false;

/**
 * Register the router's `navigate` so the bounce is an in-app navigation.
 * Returns the unregister fn (the layout calls it on unmount).
 */
export function registerSessionLostNavigator(fn: Navigator): () => void {
  navigator = fn;
  return () => {
    if (navigator === fn) navigator = null;
  };
}

function defaultFallbackNavigate(path: string): void {
  if (typeof window !== "undefined") window.location.assign(path);
}

export interface SessionLostDeps {
  readonly signOutThisDevice?: () => Promise<void>;
  /** Used only when no navigator is registered. */
  readonly fallbackNavigate?: Navigator;
}

/**
 * Leave the authenticated app: clear the shared cache, sign out with the
 * given scope, navigate to `path`. Shared by the lost-session bounce and
 * BOTH Logout buttons so a deliberate sign-out and a forced one cannot
 * interleave — while one is in progress every further report is ignored.
 */
async function leave(
  signOut: () => Promise<void>,
  path: string,
  fallbackNavigate: Navigator,
): Promise<void> {
  if (leaving) return;
  leaving = true;
  try {
    // Phase 67 (sub-decision E) — drop the WHOLE client cache so no studio's
    // data can render under another session.
    clearCache();
    try {
      await signOut();
    } catch {
      // The server may already consider this session gone (that is often
      // WHY we are here); the local copy is removed regardless, and the
      // navigation must not depend on the network.
    }
    (navigator ?? fallbackNavigate)(path);
  } finally {
    leaving = false;
  }
}

/**
 * The lost-session exit. Idempotent under a burst; see `leave`.
 */
export async function handleSessionLost(
  reason: string,
  deps?: SessionLostDeps,
): Promise<void> {
  await leave(
    deps?.signOutThisDevice ?? signOutThisDevice,
    loginPathFor(reason),
    deps?.fallbackNavigate ?? defaultFallbackNavigate,
  );
}

/**
 * Report an API failure; exits only when it IS a session loss. Fire-and-
 * forget for callers that must still surface their own error (the
 * `ApiError` they throw keeps the page's error rendering intact until the
 * bounce unmounts it).
 */
export function reportApiFailure(status: number, code: string | null): void {
  const loss = classifySessionLoss(status, code);
  if (loss !== null) void handleSessionLost(loss.reason);
}

/**
 * The deliberate sign-outs behind the two Logout buttons: this device, or
 * everywhere. Land on a plain `/login` (no stale-session banner).
 */
export async function signOutAndLeave(scope: "local" | "global"): Promise<void> {
  await leave(
    scope === "global" ? signOutEverywhere : signOutThisDevice,
    "/login",
    defaultFallbackNavigate,
  );
}

/** TEST-ONLY: drop the navigator and the latch. */
export function __resetSessionLostForTest(): void {
  navigator = null;
  leaving = false;
}
